import api from '../api';
import { studioRequestError } from './studioRequestError';
const creationRecovery = 'Retry the same action to recover this creation request; do not start a second project.';

// Retain only the issuance nonce, scoped to the signed-in account and tab.
// A lost issuance or commit response reuses the same server intent on Retry.
export async function createBlankProject(accountId, isCurrent) {
  const storageKey = `doar:studio:new:${accountId}`;
  let nonce;
  try { nonce = sessionStorage.getItem(storageKey); } catch { /* Tab-local fallback below. */ }
  if (!nonce) {
    if (!window.crypto?.randomUUID) throw new Error('Open DoARspace in a secure, supported browser to create a project.');
    nonce = pending.get(accountId) || window.crypto.randomUUID();
  }
  pending.set(accountId, nonce);
  try { sessionStorage.setItem(storageKey, nonce); } catch { /* Memory retains retry within this page. */ }
  let intent;
  try { ({ data: intent } = await api.post('/studio/intents', { version: 1, clientIntentId: nonce, operation: 'project.create', targetProjectId: null }, { timeout: 15000 })); }
  catch (error) { throw studioRequestError(error, 'Project authorization', creationRecovery); }
  if (!isCurrent()) return null;
  let data;
  try {
    ({ data } = await api.post('/studio/projects', { contractVersion: 2, documentKind: 'scene' }, { headers: { 'Idempotency-Key': intent.idempotencyKey }, timeout: 20000 }));
  } catch (error) {
    if (isCurrent() && error.response?.data?.error?.code === 'INTENT_EXPIRED') {
      pending.delete(accountId);
      try { sessionStorage.removeItem(storageKey); } catch { /* Next explicit action gets a new nonce. */ }
    }
    throw studioRequestError(error, 'Project creation', creationRecovery);
  }
  if (!isCurrent()) return null;
  if (!/^[a-f0-9]{24}$/i.test(data.projectId || '') || !/^[a-f0-9]{24}$/i.test(data.doarId || '') || !/^[a-f0-9]{24}$/i.test(data.revisionId || '') || data.state !== 'ready') throw new Error('The project result could not be verified. Retry the same creation request.');
  pending.delete(accountId);
  try { sessionStorage.removeItem(storageKey); } catch { /* Confirmed result remains server-replayable. */ }
  return data;
}
const pending = new Map();

export const listStudioProjects = (params, options = {}) => api.get('/studio/projects', { params, timeout: 15000, ...options }).then(r => r.data).catch(error => { throw studioRequestError(error, 'Project listing', 'Retry the project list. Your saved projects are unchanged.'); });
export const openStudioProject = (id, params = {}) => api.get(`/studio/projects/${id}/open`, { params, timeout: 15000 }).then(r => r.data).catch(error => { throw studioRequestError(error, 'Project opening', 'Retry this same project. Do not upload its source again.'); });
export const resumeStudioProject = () => api.get('/studio/resume', { timeout: 15000 }).then(r => r.data);
export function studioProjectLocation(target, expectedId = target?.projectId) {
  if (!/^[a-f0-9]{24}$/i.test(target?.projectId || '') || target.projectId !== expectedId) throw new Error('The project response did not match your selection. Retry opening the same project.');
  const query = new URLSearchParams();
  if (target.entryMode === 'v2' || target.assetId || target.readOnly) {
    for (const [name, value] of [['doar', target.doarId], ['revision', target.revisionId], ['asset', target.assetId]]) {
      if (value != null) { if (!/^[a-f0-9]{24}$/i.test(value)) throw new Error('The project reference could not be verified.'); query.set(name, value); }
    }
    if (target.entryMode === 'v2' && (!target.doarId || !target.revisionId)) throw new Error('The saved project needs recovery.');
  }
  if (target.entryMode === 'legacy' && !target.assetId && ['1D', '2D', '2.5D', '3D', '3.5D', '4D'].includes(target.requestedMode)) query.set('requestedMode', target.requestedMode);
  return `/space/${target.projectId}${query.toString() ? '?' + query : ''}`;
}
